import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { AuctionsApi } from '../tokens/auctions.token';
import {
  AuctionAcceptRequest,
  AuctionSummary,
  AuctionView,
  ListEnvelope,
  R2aCounterAcceptRequest,
  R2aResponse,
  ReadyToAcceptRequest,
} from '../models/financing-servicing.model';

/**
 * REST-only implementation of `AuctionsApi` (EPIC-4 / EM-500, Story 10 — EM-510).
 *
 * Calls the Story-2 `AuctionResource` (by auctionId) under `/services/embedded/api/v1/auctions`.
 * Rides the Keycloak bearer (attached by `authInterceptor`). Rule 07 / Rule 10 — no Mock; Playwright
 * intercepts via `page.route()`. Rule 09 — the raw `HttpErrorResponse` propagates for surfacing.
 */
@Injectable()
export class AuctionsRestService implements AuctionsApi {
  private readonly http = inject(HttpClient);

  /**
   * EM-1668 — the party-scoped auction LIST, live at last.
   *
   * This used to return a structured-empty envelope with a `TODO(backend)`: the Story-2
   * `AuctionResource` exposed auctions BY ID only, so the Auctions sub-tab rendered a "coming soon"
   * notice above a free-text *Open auction by ID* box. That was survivable only while the borrower
   * could obtain an id from the offer notification — and that notification dead-ended on
   * `/coming-soon`, so they could not. `GET /api/v1/auctions` now returns the caller's own auctions,
   * resolved from the session party server-side.
   */
  list(): Observable<ListEnvelope<AuctionSummary>> {
    return this.http
      .get<AuctionSummary[]>(API_PATHS.AUCTIONS)
      .pipe(map((items) => ({ available: true, items: items ?? [] })));
  }

  get(auctionId: number): Observable<AuctionView> {
    return this.http.get<AuctionView>(`${API_PATHS.AUCTIONS}/${auctionId}`);
  }

  accept(auctionId: number, request: AuctionAcceptRequest): Observable<AuctionView> {
    return this.http.post<AuctionView>(`${API_PATHS.AUCTIONS}/${auctionId}/accept`, request);
  }

  viewSecondBest(auctionId: number): Observable<AuctionView> {
    return this.http.post<AuctionView>(`${API_PATHS.AUCTIONS}/${auctionId}/view-second-best`, {});
  }

  readyToAccept(auctionId: number, request: ReadyToAcceptRequest): Observable<AuctionView> {
    return this.http.post<AuctionView>(`${API_PATHS.AUCTIONS}/${auctionId}/ready-to-accept`, request);
  }

  r2aResponses(auctionId: number): Observable<R2aResponse[]> {
    return this.http.get<R2aResponse[]>(`${API_PATHS.AUCTIONS}/${auctionId}/r2a-responses`);
  }

  r2aCounterAccept(auctionId: number, request: R2aCounterAcceptRequest): Observable<AuctionView> {
    return this.http.post<AuctionView>(`${API_PATHS.AUCTIONS}/${auctionId}/r2a-counter-accept`, request);
  }
}
